import type { Signal } from '@preact/signals'
import type { JSX } from 'preact'
import { styled } from '../setup'
import { borders, colors, components, spacing, typography } from '../theme'

const Container = styled('div')`
  margin-bottom: ${spacing.lg};
`

const Label = styled('label')`
  display: block;
  color: ${colors.textMuted};
  font-size: ${components.input.labelSize};
  font-weight: ${typography.fontWeightMedium};
  margin-bottom: ${spacing.sm};
`

const Input = styled('input')<{ hasError?: boolean }>`
  width: 100%;
  padding: ${components.input.padding};
  background: ${components.input.background};
  border: ${borders.widthMedium} solid ${(props) => (props.hasError ? colors.error : colors.border)};
  border-radius: ${components.input.borderRadius};
  color: ${colors.textPrimary};
  font-size: ${components.input.fontSize};
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;

  &:focus {
    border-color: ${(props) => (props.hasError ? colors.error : colors.borderFocus)};
    box-shadow: 0 0 0 3px ${(props) => (props.hasError ? colors.errorBg : 'rgba(22, 101, 52, 0.15)')};
  }

  &::placeholder {
    color: ${colors.textMuted};
    opacity: 0.6;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: ${colors.bgSurfaceAlt};
  }
`

const Hint = styled('div')`
  color: ${colors.textMuted};
  font-size: ${components.input.hintSize};
  margin-top: ${spacing.sm};
`

const ErrorMessage = styled('div')`
  color: ${colors.error};
  font-size: ${components.input.hintSize};
  margin-top: ${spacing.sm};
  font-weight: ${typography.fontWeightMedium};
`

interface TextInputProps {
  label: string
  signal: Signal<string>
  type?: 'text' | 'email' | 'password'
  placeholder?: string
  hint?: string
  error?: string
  disabled?: boolean
  autoComplete?: string
  autoFocus?: boolean
}

export function TextInput({
  label,
  signal,
  type = 'text',
  placeholder,
  hint,
  error,
  disabled,
  autoComplete,
  autoFocus,
}: TextInputProps) {
  return (
    <Container>
      <Label>{label}</Label>
      <Input
        type={type}
        value={signal.value}
        onInput={(e: JSX.TargetedEvent<HTMLInputElement>) => {
          signal.value = e.currentTarget.value
        }}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        hasError={!!error}
      />
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {hint && !error && <Hint>{hint}</Hint>}
    </Container>
  )
}
